import * as React from 'react'

import { Box, Text, useToggle } from '@factorialco/deprecated-design-system'
import lowerCase from 'lodash/lowerCase'

import { observer } from 'mobx-react'

import ExplotableItem from 'modules/core/components/Explotable/List/Item'
import Loading from 'modules/core/components/Loading'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import {
  actionList,
  conditionList,
} from 'modules/automations/components/AddAutomationBlockModal/actions'
import type { SelectableElement } from 'modules/automations/components/AddAutomationBlockModal/actions'
import type { SelectedAction } from 'modules/automations/components/AddAutomationBlockModal/types'
import type { Automation } from 'modules/automations/store/models/Automation'
import type {
  ITriggerCondition,
  ITriggerConditions,
  ITriggerWithConditions,
} from 'modules/automations/store/models/Trigger'

import ConditionGroup from './ConditionGroup'
import Item from './Item'
import { ConditionsGroup } from './types'

export interface Props {
  automation: Automation
  trigger: ITriggerWithConditions
  search: string
  selected: SelectedAction | null | undefined
  setSelected: (action: SelectedAction) => () => void
}

export interface State {
  conditions: ITriggerConditions[] | SelectableElement[]
  loading: boolean
}

function Items({ automation, trigger, search, selected, setSelected }: Props) {
  const [state, setState] = React.useState<State>({ loading: true, conditions: [] })

  const extensionFeatureHasConditions = () => {
    return trigger.conditions?.length !== 0
  }

  useEffect(() => {
    const fetchConditions = async () => {
      let conditions

      if (extensionFeatureHasConditions()) {
        conditions = trigger.conditions ?? []
      } else {
        conditions = await conditionList()
      }

      setState({
        loading: false,
        conditions: conditions,
      })
    }

    fetchConditions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderHeader = (title: string) => {
    return (
      <Text.Normal color="grey800" size="200" weight="semibold">
        {title}
      </Text.Normal>
    )
  }

  // TODO: Remove at the end of the epic 'automations-domain-extension'
  const filter = (elements: SelectableElement[]): SelectableElement[] => {
    const term = lowerCase(search)

    return elements.filter((element) => {
      const toMatch = lowerCase(element.text())

      return toMatch.match(term)
    })
  }

  const filterConditions = (elements: ITriggerConditions[]): ITriggerConditions[] => {
    const term = lowerCase(search)

    return elements
      .map((item: ITriggerConditions) => {
        return {
          ...item,
          conditions: item.conditions.filter((condition: ITriggerCondition) => {
            const toMatch = lowerCase(condition.label)

            return toMatch.match(term)
          }),
        }
      })
      .filter((item: ITriggerConditions) => item.conditions.length)
  }

  const Group = ({ title, children }: React.PropsWithChildren<{ title: string }>) => {
    const [open, toggle] = useToggle()

    return (
      <ExplotableItem
        expandIcon="plus-minus"
        headerBorders={false}
        opened={open}
        renderHeader={() => renderHeader(title)}
        size="small"
        toggle={toggle(!open)}
        scrollLimited={false}
      >
        <Box padding="s16" gap="s8">
          {children}
        </Box>
      </ExplotableItem>
    )
  }

  const renderItem = (action: SelectableElement, index: number): JSX.Element => {
    const isSelected = selected ? selected.key === action.key : false

    return (
      <Item
        key={action.key}
        label={action.text()}
        action={action}
        actionableType={action.actionableType}
        selected={isSelected}
        setSelected={setSelected}
      />
    )
  }

  const renderGroupedConditionItems = (group: ITriggerConditions): ConditionsGroup => {
    return {
      ...group,
      conditions: group.conditions.map((condition: ITriggerCondition) => {
        const isSelected = selected ? selected.key === condition.key : false

        return (
          <Item
            key={condition.key}
            label={condition.label}
            action={condition}
            actionableType={condition.actionable_type}
            selected={isSelected}
            setSelected={setSelected}
          />
        )
      }),
    }
  }

  const renderActions = () => {
    let triggerSlug

    if (extensionFeatureHasConditions()) {
      triggerSlug = trigger.slug
    } else {
      triggerSlug = automation.trigger?.attributes.slug
    }

    if (!triggerSlug) return null

    return (
      <Group title={i18n.t('automations.add_block.actions')}>
        {filter(actionList(triggerSlug)).map(renderItem)}
      </Group>
    )
  }

  const getItems = (): ConditionsGroup[] | JSX.Element[] => {
    if (extensionFeatureHasConditions()) {
      const filteredConditions = filterConditions(state?.conditions as ITriggerConditions[])

      return filteredConditions.map(renderGroupedConditionItems)
    } else {
      return filter(state?.conditions as SelectableElement[]).map(renderItem)
    }
  }

  const showConditions = () => {
    if (extensionFeatureHasConditions()) {
      return getItems().length > 0
    }

    return !automation.trigger?.isRecurrent
  }

  if (state?.loading) return <Loading />

  return (
    <>
      {showConditions() && (
        <ConditionGroup
          title={i18n.t('automations.add_block.conditions')}
          items={getItems()}
          renderHeader={renderHeader}
          extensionFeatureHasConditions={extensionFeatureHasConditions()}
        />
      )}
      <ul>{renderActions()}</ul>
    </>
  )
}

export default observer(Items)
